/**
 * Dynamic lights — rockets and explosions lighting the world.
 *
 * Copyright (C) 2026 Overbounce contributors
 * Licensed under the GNU General Public License v2 or later. See LICENSE.
 *
 * This only became meaningful once the map had lightmaps. Before that every
 * surface was flat-shaded and "adding light" would have been indistinguishable
 * from tinting. Now there is baked light to add to, and a rocket flying down a
 * dark corridor lights the walls as it passes — which in Quake is not just
 * decoration, it is how you see where your own rocket went.
 *
 * Values from the id source rather than taste:
 *
 *   - `cg_ents.c` adds a light at the missile's position every frame, using the
 *     weapon's `missileDlight` / `missileDlightColor`.
 *   - `cg_effects.c` gives a rocket explosion `light = 300` and
 *     `lightColor = (1, 0.75, 0)`.
 *   - `cg_localents.c` holds the explosion at full brightness for the first
 *     half of its life and then fades linearly:
 *     `light < 0.5 ? 1.0 : 1.0 - (light - 0.5) * 2`.
 *
 * The shader side is a fixed-size uniform array rather than a light list,
 * because every world material shares one set of uniforms: 85 materials each
 * carrying their own light state would mean 85 uniform buffers updated per
 * frame, and the whole point is that this is cheap.
 */

import { Vector3, Vector4 } from 'three/webgpu';
import type { Node } from 'three/webgpu';
import { positionWorld, uniform, vec3 } from 'three/tsl';
import { q3ToThree } from './renderer.js';

/** How many lights can affect the world at once. Quake's own limit is 32. */
export const MAX_DYNAMIC_LIGHTS = 8;

/** `cg_effects.c`: a rocket explosion. */
/**
 * `?missilelight` -- a multiplier on every projectile and explosion light's
 * RADIUS, not its brightness.
 *
 * The distinction is the point. `?lightscale` makes a light brighter and
 * changes nothing about WHERE its shadow lands: three's `distance` is a hard
 * cutoff, so a rocket at Quake's own 200 units lights and shadows a bubble
 * two player-heights across and nothing beyond it. On a side camera pulled
 * back across a room, a hard shadow inside a 200-unit sphere moving at 900ups
 * is easy to miss entirely -- which is what "rocket shadows are not visible"
 * turned out to mean once the two real bugs behind it were fixed.
 *
 * **Default 4**, which is a deliberate departure from id's 200 rather than an
 * oversight: at 1 the light and its shadow live in a sphere two
 * player-heights across, moving at 900ups, and on a side camera pulled back
 * across a room almost nobody ever sees one. 800 units is a rocket that
 * lights the corridor it is flying down. `?missilelight=1` puts Quake's own
 * radius back.
 *
 * Note what this does NOT change: `dynamic-lights.ts`'s constants below are
 * still id's, and the multiplier is applied at the call site in `main.ts`, so
 * the ported numbers stay readable as ported numbers.
 */
export const DEFAULT_MISSILE_LIGHT_SCALE = 4;

export function parseMissileLightScale(search: string | URLSearchParams): number {
  const params = typeof search === 'string' ? new URLSearchParams(search) : search;
  const raw = params.get('missilelight');
  if (raw === null) {
    return DEFAULT_MISSILE_LIGHT_SCALE;
  }
  const v = Number(raw);
  if (!Number.isFinite(v) || v <= 0) {
    console.warn(`[overbounce] ignoring ?missilelight=${raw}: expected a positive number`);
    return 1;
  }
  return v;
}

export const ROCKET_EXPLOSION_LIGHT = 300;
export const ROCKET_LIGHT_COLOR: [number, number, number] = [1, 0.75, 0];

/** `cg_ents.c`: the missile in flight. */
export const ROCKET_MISSILE_LIGHT = 200;

/**
 * `CG_PlayerPowerups`, cg_players.c:1839 -- a player carrying Quad glows.
 *
 *     trap_R_AddLightToScene( cent->lerpOrigin, 200 + (rand()&31), 0.2f, 0.2f, 1 );
 *
 * This is the ONLY powerup light Quake has, CTF flags aside, and it is on the
 * carrier rather than on the item. Lighting the pedestals is an addition; see
 * B6 in .agent/plans/VISUALS.md.
 */
export const QUAD_LIGHT = 200;
export const QUAD_LIGHT_COLOR: [number, number, number] = [0.2, 0.2, 1];

/**
 * The CTF flags, the "aside" in the line above. `CG_PlayerPowerups`,
 * cg_players.c:1857 and :1868 -- a flag carrier glows in their flag's colour:
 *
 *     trap_R_AddLightToScene( cent->lerpOrigin, 200 + (rand()&31), 1.0, 0.2f, 0.2f );
 *     trap_R_AddLightToScene( cent->lerpOrigin, 200 + (rand()&31), 0.2f, 0.2f, 1.0 );
 *
 * Same radius and same `rand()&31` flicker as the Quad, which is why they
 * share `QUAD_LIGHT` rather than repeating 200 twice more. The colours are
 * the flags' own and are exact: full in one channel, 0.2 in the other two.
 *
 * `cg_players.c:1879` gives the neutral flag `(1.0, 1.0, 1.0)`. Not here:
 * one-flag CTF is not a mode this game has (`game/flag-run.ts`), so a neutral
 * flag is never carried and a constant for it would be dead.
 *
 * **Where the light is placed is a deviation, and a deliberate one** -- see
 * `main.ts`'s own note at the call site. Quake puts it at `lerpOrigin`, the
 * player's own feet, because a Quake dlight casts no shadow and cannot care
 * what it is inside. This one is asked to cast, and a caster at that origin
 * is inside its own occluder -- the exact degenerate case `DynamicLight.shadows`
 * documents. It is emitted from the FLAG instead.
 */
export const FLAG_LIGHT = QUAD_LIGHT;
export const RED_FLAG_LIGHT_COLOR: [number, number, number] = [1, 0.2, 0.2];
export const BLUE_FLAG_LIGHT_COLOR: [number, number, number] = [0.2, 0.2, 1];

/**
 * A plasma bolt in flight. **NOT Quake** — a deliberate addition.
 *
 * `CG_RegisterWeapon`'s `WP_PLASMAGUN` block (cg_weapons.c:792) sets a trail
 * function, a sound and a `flashDlightColor`, and NO `missileDlight`. Only the
 * rocket and the grappling hook get one. So a glowing plasma ball is a modern
 * touch on the same track as the lava bloom, and must never be described as
 * verified — it is described here as what it is.
 *
 * The colour is not invented, though: it is the plasma gun's own
 * `flashDlightColor`, `MAKERGB(0.6, 0.6, 1.0)`. The radius is well under the
 * rocket's 200, because plasma comes ten to the second and a 200-unit light per
 * bolt turns a corridor into a strobe.
 */
export const PLASMA_MISSILE_LIGHT = 90;
export const PLASMA_LIGHT_COLOR: [number, number, number] = [0.6, 0.6, 1];

/**
 * A plasma ball detonating. **NOT Quake**, same footing as the missile light
 * above -- `CG_MissileHitWall`'s `WP_PLASMAGUN` case never sets `light`, so a
 * real plasma impact casts nothing. Before this addition the explosion flash
 * fell through to the rocket's light unconditionally regardless of which
 * weapon actually detonated, which is a worse bug than having none: a plasma
 * hit flashing rocket-orange reads as wrong in a way "no flash at all" does
 * not. Uses the same `PLASMA_LIGHT_COLOR`, at half the rocket explosion's
 * radius -- plasma's splash radius (20) is a sixth of the rocket's (120), and
 * a full 300-unit flash for it would light up a room a bolt would barely dent.
 */
export const PLASMA_EXPLOSION_LIGHT = 150;

/**
 * A light, positioned in QUAKE space like everything else the game layer emits.
 * `set` converts to three's at the boundary.
 */
export interface DynamicLight {
  origin: ArrayLike<number>;
  /** Radius; 0 means the slot is unused. */
  radius: number;
  color: ArrayLike<number>;
  /**
   * May this light cast a shadow?
   *
   * **Defaults to false, and the default is the important half.** Quake's
   * dlights cast no shadows at all, so anything here is an addition — and one
   * kind of dlight must never cast, which is the kind attached to the thing
   * carrying it.
   *
   * The Quad's light sits at the PLAYER'S ORIGIN, inside the player model,
   * and the player is a shadow caster. A light inside its own occluder is
   * degenerate: the player blocks their own glow in every direction at once and
   * throws hard black wedges radially across the floor. On q3dm6 that turned
   * the pentagram's inlaid star black and was reported, correctly, as the light
   * not reading as a uniform glow.
   *
   * A rocket in flight is the opposite case — it is out in the open with
   * nothing of its own to occlude it, and its light throwing the player's
   * shadow across a wall is exactly the effect worth having. So this is per
   * light, decided by whatever emits it, rather than a global.
   */
  shadows?: boolean;
}

export class DynamicLights {
  /** xyz = position, w = radius. */
  private readonly positions: Vector4[] = [];
  private readonly colors: Vector3[] = [];
  private readonly positionNodes: Node<'vec4'>[] = [];
  private readonly colorNodes: Node<'vec3'>[] = [];

  constructor() {
    // One uniform per slot rather than a uniformArray. Both work on the GPU;
    // this one has a type TSL can follow through a swizzle, and 16 uniforms is
    // not a number worth optimising.
    for (let i = 0; i < MAX_DYNAMIC_LIGHTS; i++) {
      const position = new Vector4(0, 0, 0, 0);
      const color = new Vector3(0, 0, 0);
      this.positions.push(position);
      this.colors.push(color);
      this.positionNodes.push(uniform(position));
      this.colorNodes.push(uniform(color));
    }
  }

  /**
   * Replace the whole light set for this frame.
   *
   * Lights are transient — a missile moves, an explosion fades — so rebuilding
   * the list each frame is simpler and cheaper than tracking identities.
   *
   * WHICH EIGHT, when there are more than eight. Originally the first eight in
   * the caller's order won and the rest were dropped, on the reasoning that a
   * light flickering in and out as the list reshuffles reads worse than one
   * that is simply absent. That reasoning is still right and the policy was
   * still wrong, and plasma is what exposed it: at ten bolts a second the list
   * fills with projectiles, and the light that gets dropped is whichever the
   * caller happened to append last — routinely the player's own Quad glow.
   *
   * So when the list overflows, the NEAREST to `viewer` win. The sort only
   * happens on overflow, so the ordinary case is untouched and stable, and the
   * lights that drop out are the distant ones whose contribution was close to
   * zero anyway. Without a viewer it falls back to the old first-eight rule.
   */
  set(lights: readonly DynamicLight[], viewer?: ArrayLike<number>): void {
    let chosen = lights;
    if (lights.length > MAX_DYNAMIC_LIGHTS && viewer) {
      const distance = (l: DynamicLight): number =>
        (l.origin[0] - viewer[0]) ** 2 +
        (l.origin[1] - viewer[1]) ** 2 +
        (l.origin[2] - viewer[2]) ** 2;
      chosen = [...lights].sort((a, b) => distance(a) - distance(b));
    }

    for (let i = 0; i < MAX_DYNAMIC_LIGHTS; i++) {
      const light = chosen[i];
      if (light) {
        // CONVERTED HERE, and this is the whole reason dynamic lights did
        // nothing for so long.
        //
        // Callers hand these over in Quake space, because everything upstream
        // -- missiles, the player, items -- lives there. But `contribution()`
        // measures against `positionWorld`, and the world group carries
        // `rotation.x = -PI/2` to get from Z-up to Y-up, so that node is in
        // THREE space. Comparing the two frames put every light hundreds of
        // units from where the geometry thought it was, the `1 - dist/radius`
        // falloff clamped to zero everywhere, and the effect silently did
        // nothing at all -- no error, no warning, just an unlit wall.
        const [x, y, z] = q3ToThree(light.origin[0], light.origin[1], light.origin[2]);
        this.positions[i].set(x, y, z, light.radius);
        this.colors[i].set(light.color[0], light.color[1], light.color[2]);
      } else {
        this.positions[i].w = 0;
      }
    }
  }

  /**
   * Read a slot back, for tests and for debugging.
   *
   * `xyz` is in THREE space, because that is the space the uniform is in and
   * pretending otherwise would hide the conversion this class exists to do.
   * `w` is the radius; 0 means unused.
   */
  slot(index: number): Readonly<Vector4> {
    return this.positions[index];
  }

  /**
   * The TSL node for the light reaching this fragment.
   *
   * A linear `1 - dist/radius` falloff, which is what Quake's own dlight does:
   * not physically correct, but it is the look, and an inverse-square falloff
   * makes a 300-unit rocket light vanish almost immediately.
   */
  contribution(): Node<'vec3'> {
    // Annotated rather than inferred: TSL's node types are precise enough that
    // `vec3(0,0,0)` infers as a VarNode and `.add()` widens to a plain Node, so
    // an inferred accumulator cannot be reassigned to its own sum.
    let total: Node<'vec3'> = vec3(0, 0, 0);

    for (let i = 0; i < MAX_DYNAMIC_LIGHTS; i++) {
      const packed = this.positionNodes[i];
      const color = this.colorNodes[i];

      // A linear falloff, clamped at zero. An unused slot has radius 0, and
      // `max(0.001)` keeps the divide finite so it contributes nothing without
      // needing a per-fragment branch over every slot.
      const dist = positionWorld.distance(packed.xyz);
      const falloff = dist.div(packed.w.max(0.001)).oneMinus().max(0);

      total = total.add(color.mul(falloff));
    }

    return total;
  }
}
